import { useLabel } from '@react-aria/label';
import type { HTMLProps, ReactNode } from 'react';
import { Input } from 'react-aria-components';

import { InlineError } from '../Feedback/InlineError';
import { classNames } from '../Utils/classNames';
import type { InvalidFieldProps } from '../Utils/commonTypes';

export type TextFieldProps = HTMLProps<HTMLInputElement> &
  InvalidFieldProps & {
    helpText?: string;
    hideLabel?: boolean;
    inputClassName?: string;
    labelClassName?: string;
    /** A component to be rendered on the left edge of the `TextField`. */
    connectedLeft?: ReactNode;

    /** A component to be rendered on the right edge of the `TextField`. */
    connectedRight?: ReactNode;

    inputSize?: 'md' | 'lg';

    shouldUseReactAriaInput?: boolean;
  };

export function TextField(props: TextFieldProps) {
  let {
    label: labelText,
    hideLabel,
    helpText,
    isInvalid,
    invalidText,
    invalidTextTestId,
    connectedLeft,
    connectedRight,
    type = 'text',
    inputSize = 'md',
    disabled,
    inputClassName,
    labelClassName,
    ref,
    shouldUseReactAriaInput = false,
    placeholder,
    ...inputProps
  } = props;

  const fallBackErrorTestId = `${inputProps.name}Error`;

  if (!invalidTextTestId) {
    invalidTextTestId = fallBackErrorTestId;
  }

  let computedProps: Omit<HTMLProps<HTMLInputElement>, 'ref'> = {};
  const helpTextElementID = `${inputProps.name}-help-text`;

  if (helpText) {
    computedProps['aria-describedby'] = helpTextElementID;
  }

  const { labelProps, fieldProps } = useLabel(props);

  const InputComponent = shouldUseReactAriaInput ? Input : 'input';

  return (
    <div className="h-fit w-full">
      <label
        className={classNames(
          {
            'sr-only': hideLabel,
            'text-error-6': isInvalid,
          },
          labelClassName
        )}
        {...labelProps}
      >
        {labelText}
      </label>
      <div className="relative w-full">
        {connectedLeft && (
          <div className="absolute inset-y-0 left-[14px] flex items-center">
            {connectedLeft}
          </div>
        )}
        <InputComponent
          type={type}
          className={classNames(
            'w-full rounded-md py-2',
            {
              'border-gray-5': !isInvalid && !disabled,
              'border-error-6 text-error-6 placeholder-error-6 focus:border-error-6 focus:outline-none focus:ring-error-6':
                isInvalid,
              'pl-9': connectedLeft,
              'pr-9': connectedRight,
              'cursor-not-allowed border border-gray-5 bg-gray-2 placeholder:text-gray-7':
                disabled,
              'h-10': inputSize === 'md',
              'h-12': inputSize === 'lg',
            },
            inputClassName
          )}
          {...(labelText ? fieldProps : {})}
          {...computedProps}
          {...inputProps}
          {...(isInvalid
            ? {
                'aria-invalid': true,
                'aria-describedby': invalidTextTestId,
              }
            : {})}
          disabled={disabled}
          placeholder={placeholder}
        />
        {connectedRight && (
          <div className="absolute inset-y-0 right-[14px] my-[1px] flex items-center pl-3">
            {connectedRight}
          </div>
        )}
      </div>
      {helpText && !isInvalid && (
        <span className="text-xs text-gray-8" id={helpTextElementID}>
          {helpText}
        </span>
      )}
      {isInvalid && (
        <InlineError id={invalidTextTestId} size="xs" as="span">
          {invalidText}
        </InlineError>
      )}
    </div>
  );
}
